import { Component, NgZone, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuthenticationService } from '../../domain/services/auth/authentication.service';

const TOKEN_NAME = 'appToken';
const WARN_MS = 2 * 60 * 1000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'wheel', 'touchstart'];

/** exp/iat (seconds) from the stored JWT, or null when there is no usable token. */
function readTokenTimes(): { exp: number; iat: number } | null {
  try {
    const raw = localStorage.getItem(TOKEN_NAME);
    const token = raw ? JSON.parse(raw) : null;
    if (!token || typeof token !== 'string') return null;
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp && payload.iat ? { exp: payload.exp, iat: payload.iat } : null;
  } catch {
    return null;
  }
}

/**
 * Sliding session by inactivity. The JWT has a fixed lifetime, so while the user is active
 * (any click/key/mouse since the token was issued) we swap it for a fresh one once it is past
 * half-life. If they go idle, a banner counts down the last 2 minutes and then signs them out.
 */
@Component({
  selector: 'app-session-expiry-banner',
  standalone: true,
  imports: [MatButtonModule, MatIconModule],
  template: `
    @if (secondsLeft() !== null) {
      <div class="session-banner" role="alert" aria-live="assertive">
        <mat-icon>schedule</mat-icon>
        <span>For your security, you will be signed out in <b>{{ countdown() }}</b> due to inactivity.</span>
        <button mat-flat-button (click)="staySignedIn()">Stay signed in</button>
      </div>
    }
  `,
  styles: [`
    .session-banner {
      position: fixed; top: 16px; left: 50%; transform: translateX(-50%); z-index: 10000;
      display: flex; align-items: center; gap: 12px; padding: 10px 12px 10px 18px;
      background: #FFF3E0; color: #7A3E00; border: 1px solid #EF6C00; border-radius: 10px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, .18); font-size: 15px; max-width: calc(100vw - 32px);
    }
    .session-banner mat-icon { color: #EF6C00; flex: none; }
    .session-banner button { --mdc-filled-button-container-color: #EF6C00; --mdc-filled-button-label-text-color: #fff; flex: none; }
  `],
})
export class SessionExpiryBannerComponent implements OnInit, OnDestroy {
  readonly secondsLeft = signal<number | null>(null);
  private lastActivity = Date.now();
  private refreshing = false;
  private timer?: ReturnType<typeof setInterval>;
  private readonly onActivity = () => { this.lastActivity = Date.now(); };

  constructor(private zone: NgZone, private router: Router, private auth: AuthenticationService) {}

  ngOnInit(): void {
    // ponytail: 1s tick outside Angular; only re-enters the zone when the banner changes.
    this.zone.runOutsideAngular(() => {
      ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, this.onActivity, { passive: true, capture: true }));
      this.timer = setInterval(() => this.tick(), 1000);
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, this.onActivity, { capture: true }));
  }

  countdown(): string {
    const s = this.secondsLeft() ?? 0;
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  staySignedIn(): void {
    this.lastActivity = Date.now();
    this.refresh();
  }

  private tick(): void {
    const t = readTokenTimes();
    if (!t) return this.show(null);
    const left = t.exp * 1000 - Date.now();
    if (left <= 0) return this.expire();
    const halfLife = (t.exp - t.iat) * 500;
    if (this.lastActivity > t.iat * 1000 && left < halfLife) this.refresh();
    this.show(left <= WARN_MS ? Math.ceil(left / 1000) : null);
  }

  private refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.auth.refreshSession().subscribe({
      // A 401 here is handled by authErrorInterceptor (back to login).
      complete: () => { this.refreshing = false; this.tick(); },
      error: () => { this.refreshing = false; },
    });
  }

  private expire(): void {
    this.show(null);
    this.zone.run(() => {
      this.auth.logout();
      this.router.navigate(['login']);
    });
  }

  private show(seconds: number | null): void {
    if (seconds !== this.secondsLeft()) this.zone.run(() => this.secondsLeft.set(seconds));
  }
}
